import { useContext, useCallback } from "react";
import { ConfiguratorContext } from "../context";
import useConfiguratorData from "./useConfiguratorData";
import useDealershipsData from "./useDealershipsData";
import usePersonalData from "./usePersonalData";
import usePriceData, { computeFinancedTotal } from "./usePriceData";
import { cleanRutFormat } from "../../utils";
import Axios from "axios";

/**
 * Contador de peticiones compartido por todas las instancias del hook.
 * Se usa para descartar respuestas fuera de orden: gana la última petición
 * emitida, no la última respuesta recibida.
 */
let mafRequestSeq = 0;

/**
 * Indica si la simulación MAF guardada corresponde a la configuración actual
 * (misma versión, mismo monto financiado, mismo pie/cuotas/plan renueve).
 * Es una función pura para poder evaluarla sobre cualquier snapshot de estado.
 */
// Comparación de montos con tolerancia de $1: evita que un redondeo deje una
// simulación válida marcada como obsoleta para siempre. Un monto no numérico
// se considera distinto, de modo que se vuelva a simular.
const amountsMatch = (a, b) =>
    Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) < 1;

export const isMafSimulationCurrent = (state) => {
    const mafData = state.mafSimulationData || {};
    const simulatedFor = mafData.simulated_for;

    if (!simulatedFor || !state.selectedVersion) return false;
    if (simulatedFor.version_id !== state.selectedVersion.id) return false;

    return (
        amountsMatch(simulatedFor.car_amount, computeFinancedTotal(state, true)) &&
        simulatedFor.downpayment_percentage === parseFloat(mafData.downpayment_percentage) &&
        simulatedFor.number_fees === parseInt(mafData.number_fees) &&
        simulatedFor.smart_buy === mafData.smart_buy
    );
};

/**
 * Indica si hay un resultado MAF completo Y vigente para la configuración actual.
 * Si devuelve false los valores de financiamiento no deben guardarse en el lead.
 */
export const hasCurrentMafSimulation = (state) => {
    const mafData = state.mafSimulationData || {};

    return !!(
        mafData.hasResult &&
        mafData.fee_value &&
        mafData.credit_total_cost &&
        mafData.equivalent_annual_load &&
        mafData.downpayment_amount &&
        isMafSimulationCurrent(state)
    );
};

/**
 * Hook para simulación de financiamiento MAF
 * Maneja simulaciones de crédito con configuración de pie y cuotas
 */
const useMAFSimulationData = () => {
    const [state, setState] = useContext(ConfiguratorContext);
    const { getSelectedVersion, getSelectedModel } = useConfiguratorData();
    const { selectedDealership } = useDealershipsData();
    const { getPersonalData } = usePersonalData();
    const { getFinancedTotal } = usePriceData();

    const mafData = state.mafSimulationData;

    // Actualiza los datos de simulación MAF
    const updateMAFData = useCallback(
        (newData) => {
            setState((prevState) => ({
                ...prevState,
                mafSimulationData: {
                    ...prevState.mafSimulationData,
                    ...newData,
                },
            }));
        },
        [setState]
    );

    /**
     * Aplica el desenlace de una petición sólo si sigue siendo relevante:
     * descarta respuestas de una versión que el usuario ya cambió y respuestas
     * superadas por una petición posterior. Sin esta guarda, una simulación en
     * vuelo de la versión anterior pisa el estado recién reseteado y termina
     * guardándose en el lead junto a la versión nueva.
     */
    const applyMAFResult = useCallback(
        (versionId, requestId, newData) => {
            setState((prevState) => {
                if (prevState.selectedVersion?.id !== versionId) return prevState;
                if ((prevState.mafSimulationData.request_id || 0) > requestId) return prevState;

                return {
                    ...prevState,
                    mafSimulationData: {
                        ...prevState.mafSimulationData,
                        ...newData,
                        request_id: requestId,
                    },
                };
            });
        },
        [setState]
    );

    // Actualiza solo el porcentaje de pie (evita loops de auto-simulación)
    const updateDownpayment = useCallback(
        (downpayment_percentage) => {
            updateMAFData({ downpayment_percentage });
        },
        [updateMAFData]
    );

    // Actualiza solo el número de cuotas (evita loops de auto-simulación)
    const updateFees = useCallback(
        (number_fees) => {
            updateMAFData({ number_fees });
        },
        [updateMAFData]
    );

    // Marca que la simulación tiene resultado
    const setHasResult = useCallback(
        (hasResult, resultData = null, silent = false) => {
            updateMAFData({ hasResult: silent ? false : hasResult, resultData });
        },
        [updateMAFData]
    );

    // Controla el estado de carga de la simulación
    const setIsLoading = useCallback(
        (isLoading) => {
            updateMAFData({ isLoading });
        },
        [updateMAFData]
    );

    // Simular crédito con API real
    const simulateCredit = useCallback(
        async (overrideData = {}, isAutoSimulation = false) => {
            const version = getSelectedVersion();
            const model = getSelectedModel();
            const personalData = getPersonalData();
            const financedTotal = getFinancedTotal(true);

            // Validación de datos requeridos
            if (!version || !model) {
                if (!isAutoSimulation) {
                    console.warn("MAF Simulation: Missing version or model data");
                }
                return;
            }

            // Parámetros efectivos de esta simulación. Se calculan una sola vez
            // para que la petición, el resultado y `simulated_for` no puedan
            // quedar descoordinados entre sí.
            const downpaymentPercentage = parseFloat(
                overrideData.downpayment_percentage ?? state.mafSimulationData.downpayment_percentage
            );
            const numberFees = parseInt(
                overrideData.number_fees ?? state.mafSimulationData.number_fees
            );
            const smartBuy = overrideData.smart_buy ?? state.mafSimulationData.smart_buy;
            const downPayment = Math.round((downpaymentPercentage / 100) * financedTotal);

            // Identificación de la petición para poder descartar respuestas obsoletas
            const versionId = version.id;
            const requestId = ++mafRequestSeq;

            setIsLoading(true);
            updateMAFData({ error: null });

            const simulationData = {
                // Datos del vehículo
                car_amount: financedTotal,
                vehicleAmount: financedTotal,
                sap_code: version.sap_id ? version.sap_id : 0,
                option_code: version.option_code ? version.option_code : "",
                model_id: model.model_id,

                // Datos de financiamiento
                downpayment_percentage: downpaymentPercentage,
                number_fees: numberFees,
                smart_buy: smartBuy,

                // Datos personales (v1)
                full_name: personalData.full_name,
                email: personalData.email,
                rut: cleanRutFormat(personalData.rut),
                phone_number: personalData.phone_number,

                // Datos personales (v2)
                first_name: personalData.first_name,
                last_name: personalData.last_name,
                clientRut: cleanRutFormat(personalData.rut),
                phone: personalData.phone_number,

                // Cálculos
                downPayment: downPayment,
                creditAmount: financedTotal - downPayment,

                // Dealer info
                dealership_id: selectedDealership?.rut || "",
            };

            try {
                const response = await Axios.post(
                    "/api/vehicles/bnp/simulate_credit/",
                    simulationData
                );
                const data = response.data;

                // Actualizar con los datos de respuesta (compatible con ambas estructuras v1/v2)
                const resultData = {
                    hasResult: true, // Siempre mostrar resultado cuando la simulación es exitosa
                    isLoading: false,
                    error: null,

                    // Response data
                    credit_total_cost: data.credit_total_cost || data.totalCost,
                    downpayment_amount: data.downpayment_amount || data.downPayment,
                    equivalent_annual_load: data.equivalent_annual_load || data.cae,
                    future_value_guaranteed: data.future_value_guaranteed || data.vfg,
                    fee_value: data.fee_value || data.repaymentAmount,
                    rate_interest: data.rate_interest,
                    simulation_id: data.simulationId,
                    rate: data.rate,

                    // Inputs efectivos de esta simulación
                    downpayment_percentage: downpaymentPercentage,
                    number_fees: numberFees,
                    smart_buy: smartBuy,

                    // Calculated fields
                    car_amount: financedTotal,
                    credit_amount: financedTotal - downPayment,

                    // Configuración que respalda estos valores
                    simulated_for: {
                        version_id: versionId,
                        car_amount: financedTotal,
                        downpayment_percentage: downpaymentPercentage,
                        number_fees: numberFees,
                        smart_buy: smartBuy,
                    },
                };

                applyMAFResult(versionId, requestId, resultData);
            } catch (error) {
                console.error("MAF Simulation API error:", error);

                // Solo actualizar error si NO es auto-simulación
                if (!isAutoSimulation) {
                    applyMAFResult(versionId, requestId, {
                        isLoading: false,
                        error: error.response?.data || error.message,
                    });
                } else {
                    // En auto-simulación solo limpiar el loading
                    applyMAFResult(versionId, requestId, { isLoading: false });
                }
            }
        },
        [
            state.mafSimulationData,
            updateMAFData,
            applyMAFResult,
            setIsLoading,
            getSelectedVersion,
            getSelectedModel,
            selectedDealership,
            getPersonalData,
            getFinancedTotal,
        ]
    );

    // Resetear para edición (mantener los valores pero ocultar resultado)
    const resetForEdit = useCallback(() => {
        updateMAFData({ hasResult: false, isLoading: false });
    }, [updateMAFData]);

    // Obtener datos actuales
    const getMAFData = useCallback(() => {
        return mafData;
    }, [mafData]);

    // Obtener configuración de inputs (similar a r9Props en PaymentOptions v1)
    const getInputConfig = useCallback(() => {
        return {
            minDownpayment: 31,
            maxDownpayment: 40,
            minFees: 24,
            maxFees: 36,
        };
    }, []);

    // Verifica si los datos de simulación MAF están completos y vigentes
    const hasCompleteSimulation = useCallback(() => hasCurrentMafSimulation(state), [state]);

    // Indica si la simulación guardada corresponde a la configuración actual
    const isSimulationCurrent = isMafSimulationCurrent(state);

    // Simula crédito si los datos no están completos o quedaron obsoletos
    // (cambio de versión, de accesorios, de mantenciones, etc.)
    const ensureSimulation = useCallback(async () => {
        if (!hasCompleteSimulation()) {
            console.warn("MAF: Simulación ausente u obsoleta, ejecutando simulación de respaldo");
            await simulateCredit({
                downpayment_percentage: mafData.downpayment_percentage,
                number_fees: mafData.number_fees,
            });
            return true; // Indica que se ejecutó simulación
        }
        return false; // Indica que no fue necesario simular
    }, [hasCompleteSimulation, simulateCredit, mafData]);

    return {
        mafData,
        updateMAFData,
        updateDownpayment,
        updateFees,
        setHasResult,
        setIsLoading,
        simulateCredit,
        resetForEdit,
        getMAFData,
        getInputConfig,
        hasCompleteSimulation,
        isSimulationCurrent,
        ensureSimulation,
    };
};

export default useMAFSimulationData;
